import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Bot, TrendingUp, AlertTriangle, Users, DollarSign } from 'lucide-react';

const Stat = ({ label, value, icon: Icon }) => (
  <div className="bg-white rounded-3xl p-5 card-soft">
    <div className="flex items-center gap-2 text-navy/50">
      <Icon size={15} className="text-gold" strokeWidth={1.75} />
      <p className="text-xs uppercase tracking-widest">{label}</p>
    </div>
    <p className="font-heading text-3xl mt-1">{value}</p>
  </div>
);

export default function AdminAIUsage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [credits, setCredits] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) return;
    setLoading(true);
    Promise.all([
      http.get(`/admin/ai-usage?days=${days}`).then((r) => setData(r.data)).catch(() => {}),
      http.get(`/admin/ai-usage/credits?days=${days}`).then((r) => setCredits(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [user, days]);

  if (!user?.is_admin) return <Navigate to="/app" replace />;

  const maxTrend = Math.max(1, ...((data?.trend || []).map((t) => t.count)));

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-6" data-testid="admin-ai-usage-page">
      <Link to="/app/admin" data-testid="ai-usage-back" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
        <ArrowLeft size={14} /> Back to admin
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">AI Usage Insights</p>
          <h1 className="font-heading text-3xl mt-1">Coach usage &amp; cap</h1>
          <p className="text-navy/50 text-sm mt-1">
            Daily message counts per member (aggregate only — no chat content). Cap is{' '}
            <span className="font-semibold text-navy">{data?.cap ?? 50}/day</span> across both tiers.
          </p>
        </div>
        <div className="flex gap-1 bg-white rounded-full p-1 card-soft" data-testid="ai-usage-range">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`ai-usage-range-${d}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-smooth ${days === d ? 'bg-navy text-white' : 'text-navy/60 hover:bg-beige'}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="text-navy/60">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <Stat label="Messages today" value={data.totals.messages_today} icon={Bot} />
            <Stat label="Est. spend today" value={`$${(data.totals.est_cost_today ?? 0).toFixed(2)}`} icon={DollarSign} />
            <Stat label="Active users today" value={data.totals.active_users_today} icon={Users} />
            <Stat label="Hit cap today" value={data.totals.cap_hits_today} icon={AlertTriangle} />
          </div>

          {credits && (
            <div className="bg-navy text-white rounded-3xl p-6 card-soft" data-testid="ai-usage-credits-card">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                <p className="font-heading text-lg flex items-center gap-2"><Bot size={17} className="text-gold" /> All AI features (Universal Key)</p>
                <p className="text-xs text-white/60">A daily email digest is sent to admins automatically</p>
              </div>
              <p className="text-xs text-white/50 mb-4">Every AI call across the app — coach, affirmations, journal reflections, course generation & more.</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-white/5 rounded-2xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-white/50">Calls today</p>
                  <p className="font-heading text-2xl mt-1" data-testid="credits-calls-today">{credits.totals.calls_today}</p>
                </div>
                <div className="bg-white/5 rounded-2xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-white/50">Est. spend today</p>
                  <p className="font-heading text-2xl mt-1">${(credits.totals.est_cost_today ?? 0).toFixed(2)}</p>
                </div>
                <div className="bg-white/5 rounded-2xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-white/50">Calls ({credits.days}d)</p>
                  <p className="font-heading text-2xl mt-1">{credits.totals.calls_range}</p>
                </div>
                <div className="bg-white/5 rounded-2xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-white/50">Est. spend ({credits.days}d)</p>
                  <p className="font-heading text-2xl mt-1">${(credits.totals.est_cost_range ?? 0).toFixed(2)}</p>
                </div>
              </div>
              {credits.features?.length > 0 && (
                <div className="mt-4 divide-y divide-white/10">
                  {credits.features.slice(0, 8).map((f) => (
                    <div key={f.feature} className="py-2 flex items-center justify-between text-sm" data-testid={`credits-feature-${f.feature}`}>
                      <span className="text-white/80 truncate">{f.feature}</span>
                      <span className="text-white/60 shrink-0">{f.calls} calls · ${f.est_cost.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}

              {credits.month && (
                <div className="mt-5 bg-white/5 rounded-2xl p-4" data-testid="credits-budget">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-sm font-semibold">Monthly spend estimate</p>
                    {credits.month.budget_usd > 0 ? (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${credits.month.over_budget ? 'bg-red-500 text-white' : credits.month.on_track_to_exceed ? 'bg-gold text-navy' : 'bg-white/10 text-white/70'}`}>
                        {credits.month.over_budget ? 'Over budget' : credits.month.on_track_to_exceed ? 'On track to exceed' : 'Within budget'}
                      </span>
                    ) : (
                      <span className="text-[10px] text-white/40">Set AI_USAGE_MONTHLY_BUDGET_USD to enable alerts</span>
                    )}
                  </div>
                  <p className="text-sm text-white/70 mt-2">
                    Month to date: <span className="font-semibold text-white">${(credits.month.mtd_est_cost ?? 0).toFixed(2)}</span>
                    {' · '}Projected: <span className="font-semibold text-white">${(credits.month.projected_est_cost ?? 0).toFixed(2)}</span>
                    {credits.month.budget_usd > 0 && <> {' · '}Budget: ${(credits.month.budget_usd ?? 0).toFixed(2)}</>}
                  </p>
                  {credits.month.budget_usd > 0 && (
                    <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                      <div className={`h-full rounded-full ${credits.month.over_budget ? 'bg-red-500' : 'bg-gold'}`}
                        style={{ width: `${Math.min(100, credits.month.pct_of_budget ?? 0)}%` }} />
                    </div>
                  )}
                </div>
              )}

              {credits.top_users?.length > 0 && (
                <div className="mt-5" data-testid="credits-top-users">
                  <p className="text-sm font-semibold mb-2">Top AI users ({credits.days}d)</p>
                  <div className="divide-y divide-white/10">
                    {credits.top_users.map((u) => (
                      <div key={u.user_id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`credits-user-${u.email}`}>
                        <div className="min-w-0">
                          <p className="text-white/85 truncate">{u.name}</p>
                          <p className="text-[11px] text-white/45 truncate">{u.email} · {u.role}</p>
                        </div>
                        <span className="text-white/70 shrink-0 tabular-nums">{u.calls} calls · ${u.est_cost.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-white/40 mt-4">
                Alerts admins by email once daily, and immediately if usage crosses {credits.alert_threshold_calls} calls/day.
                Estimates only — your exact balance is in Emergent → Profile → Universal Key.
              </p>
            </div>
          )}

          <div className="bg-white rounded-3xl p-6 card-soft">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="font-heading text-lg flex items-center gap-2"><TrendingUp size={17} className="text-gold" /> Messages per day</p>
              <p className="text-sm text-navy/60">
                Est. spend ({data.days}d): <span className="font-semibold text-navy">${(data.totals.est_cost_range ?? 0).toFixed(2)}</span>
                <span className="text-xs text-navy/40"> · ~${(data.cost_per_msg ?? 0.015).toFixed(3)}/msg</span>
              </p>
            </div>
            <div className="flex items-end gap-2 h-32">
              {data.trend.map((t) => (
                <div key={t.day} className="flex-1 flex flex-col items-center gap-1" title={`${t.day}: ${t.count} msgs · ~$${(t.est_cost ?? 0).toFixed(2)}`}>
                  <div className="w-full bg-gold/80 rounded-t" style={{ height: `${(t.count / maxTrend) * 100}%`, minHeight: t.count ? 4 : 0 }} />
                  <span className="text-[9px] text-navy/40">{t.day.slice(5)}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-navy/40 mt-3">Estimate only (messages × ~${(data.cost_per_msg ?? 0.015).toFixed(3)}). Actual spend shows in Profile → Universal Key.</p>
          </div>

          <div className="bg-white rounded-3xl p-6 card-soft">
            <p className="font-heading text-lg mb-1">Cap message A/B variants</p>
            <p className="text-xs text-navy/50 mb-4">Each user consistently sees one variant. Compare which reads best as more testers hit the cap.</p>
            <div className="space-y-3">
              {data.variants.map((v) => (
                <div key={v.id} className="border border-navy/5 rounded-2xl p-4" data-testid={`ai-usage-variant-${v.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-navy/70">{v.id.replace('_', ' ')}</span>
                    <span className="text-xs bg-beige text-navy px-2 py-0.5 rounded-full">{v.hits_today} today</span>
                  </div>
                  <p className="text-sm text-navy/70 leading-relaxed">{v.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 card-soft">
            <p className="font-heading text-lg mb-3">Today&apos;s users ({data.users.length})</p>
            {data.users.length === 0 ? (
              <p className="text-sm text-navy/50">No AI Coach activity yet today.</p>
            ) : (
              <div className="divide-y divide-navy/5">
                {data.users.map((u) => (
                  <div key={u.user_id} className="py-3 flex items-center justify-between gap-3" data-testid={`ai-usage-user-${u.email}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      <p className="text-xs text-navy/50 truncate">{u.email} · {u.role}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {u.over_cap && <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full">At cap</span>}
                      {!u.over_cap && u.near_cap && <span className="text-[10px] bg-gold text-white px-2 py-0.5 rounded-full">Near cap</span>}
                      <span className="font-heading text-lg tabular-nums">{u.total}</span>
                      <span className="text-xs text-navy/40">/{data.cap}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
